// Location.js

const cleanString = (value) => String(value || '').trim();

const joinAddressParts = (parts = []) => parts
  .map((part) => cleanString(part))
  .filter(Boolean)
  .join(', ');

const UNITED_STATES_ALIASES = new Set([
  'us',
  'usa',
  'u.s.',
  'u.s.a.',
  'united states',
  'united states of america',
]);

function isUnitedStatesCountry(value) {
  return UNITED_STATES_ALIASES.has(cleanString(value).toLowerCase());
}

/**
 * Formats a stored location value for read-only display.
 * Prefers the full formatted label when available.
 */
export function formatLocationDisplayValue(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string') return cleanString(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => formatLocationDisplayValue(entry))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value !== 'object') return String(value);

  const titleFull = cleanString(value.titleFull);
  if (titleFull) return titleFull;

  const formattedAddress = cleanString(value.formattedAddress)
    || cleanString(value.formatted_address)
    || cleanString(value.address);
  if (formattedAddress) return formattedAddress;

  const description = cleanString(value.description);
  if (description) return description;

  const structured = value.structured_formatting && typeof value.structured_formatting === 'object'
    ? value.structured_formatting
    : null;
  const mainText = cleanString(value.main_text || structured?.main_text);
  const secondaryText = cleanString(value.secondary_text || structured?.secondary_text);
  const placesLabel = joinAddressParts([mainText, secondaryText]);
  if (placesLabel) return placesLabel;

  const streetLine = [
    cleanString(value.streetNumber),
    cleanString(value.route),
  ].filter(Boolean).join(' ');

  const regionLine = [
    cleanString(value.city),
    [cleanString(value.state), cleanString(value.zip)].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const formatted = joinAddressParts([
    streetLine,
    regionLine,
    cleanString(value.country),
  ]);
  if (formatted) return formatted;

  return cleanString(value.title);
}

/**
 * Formats a location for letterhead / purchase-order style mailing address.
 * Uses structured Google parts when present; omits county and domestic US country.
 *
 * Example:
 *   505 24th Street West
 *   Billings, MT 59102
 *
 * @param {unknown} value
 * @returns {string} Multi-line string (newline-separated) or empty.
 */
export function formatLocationPostalValue(value) {
  if (value == null || value === '') return '';

  if (typeof value === 'string') return cleanString(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => formatLocationPostalValue(entry))
      .filter(Boolean)
      .join('\n\n');
  }

  if (typeof value !== 'object') return String(value);

  const streetLine = [
    cleanString(value.streetNumber),
    cleanString(value.route),
  ].filter(Boolean).join(' ');

  const unit = cleanString(value.subpremise);
  const streetWithUnit = unit
    ? (streetLine ? `${streetLine}, ${unit}` : unit)
    : streetLine;

  const city = cleanString(value.city);
  const state = cleanString(value.state);
  const zip = cleanString(value.zip);
  const cityStateZip = [
    city,
    [state, zip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');

  const country = cleanString(value.country);
  const includeCountry = country && !isUnitedStatesCountry(country);

  const lines = [
    streetWithUnit,
    cityStateZip,
    includeCountry ? country : '',
  ].filter(Boolean);

  if (lines.length) return lines.join('\n');

  // Structured parts missing — fall back to a cleaned Google label without county noise.
  const fallback = cleanString(value.description)
    || cleanString(value.result?.description)
    || cleanString(value.titleFull)
    || cleanString(value.title);

  return fallback;
}

export const getApproxLocationByIP = async () => {
  try {
    const res = await fetch('https://ipwho.is');
    const data = await res.json();

    if (data.success) {
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.city,
        region: data.region,
        country: data.country,
      };
    } else {
      console.warn('IP lookup failed:', data.message);
      return null;
    }
  } catch (err) {
    console.error('IP lookup error:', err);
    return null;
  }
};
